interface BinanceKline {
  openTime: number;
  open: string;
  high: string;
  low: string;
  close: string;
  volume: string;
  closeTime: number;
  quoteAssetVolume: string;
  numberOfTrades: number;
  takerBuyBaseAssetVolume: string;
  takerBuyQuoteAssetVolume: string;
  ignore: string;
}

export interface CandleData {
  timestamp: number;
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface TechnicalIndicators {
  sma20: number;
  sma50: number;
  ema200: number;
  rsi: number;
  atr: number;
  supertrend: number;
  inUptrend: boolean;
  bbUpper: number;
  bbMiddle: number;
  bbLower: number;
}

export interface FundingData {
  timestamp: number;
  fundingRate: number;
  fundingTime: number;
}

export interface OpenInterestData {
  timestamp: number;
  openInterest: number;
  symbol: string;
}

export interface PremiumIndexData {
  timestamp: number;
  premiumIndex: number;
}

export interface PXOIData {
  openInterest: number;
  fundingRate: number;
  premiumIndex: number;
  // Variações calculadas
  priceChange8h: number;
  priceChange12h: number;
  priceChange24h: number;
  oiChange8h: number;
  oiChange12h: number;
  oiChange24h: number;
  priceDiff8h: number;
  priceDiff12h: number;
  priceDiff24h: number;
  funding24h: number;
  fundingDelta24h: number;
}

export interface EnrichedCandleData extends CandleData, TechnicalIndicators, PXOIData {}

export interface TopTraderRatioData {
  timestamp: number;
  longAccount: number;
  shortAccount: number;
  longShortRatio: number;
}

export interface LiquidationData {
  timestamp: number;
  side: 'BUY' | 'SELL';
  price: number;
  quantity: number;
  totalUSD: number;
}

export interface PremiumIndexResponse {
  markPrice: number;
  indexPrice: number;
  premiumRate: number;
  fundingRate: number;
  nextFundingTime: number;
}

class BinanceService {
  private baseUrl = 'https://api.binance.com/api/v3';
  private futuresUrl = 'https://fapi.binance.com/fapi/v1';
  private futuresDataUrl = 'https://fapi.binance.com/futures/data';

  async fetchKlines(
    symbol: string = 'BTCUSDT',
    interval: string = '1h',
    limit: number = 1000,
    startTime?: number,
    endTime?: number
  ): Promise<CandleData[]> {
    try {
      const params = new URLSearchParams({
        symbol,
        interval,
        limit: limit.toString()
      });

      if (startTime) params.append('startTime', startTime.toString());
      if (endTime) params.append('endTime', endTime.toString());

      const response = await fetch(`${this.baseUrl}/klines?${params}`);
      if (!response.ok) {
        throw new Error(`Binance API error: ${response.status}`);
      }

      const raw = await response.json();
      
      // Binance returns an array of arrays, not objects
      return (raw as any[]).map((kline: any[]): CandleData => ({
        timestamp: kline[0],
        date: new Date(kline[0]).toISOString(),
        open: parseFloat(kline[1]),
        high: parseFloat(kline[2]),
        low: parseFloat(kline[3]),
        close: parseFloat(kline[4]),
        volume: parseFloat(kline[5])
      }));
    } catch (error) {
      console.error('Error fetching Binance data:', error);
      throw error;
    }
  }

  getTimeRangeFromPeriod(period: string): { days: number; startTime: number; endTime: number } {
    const now = Date.now();
    const oneDayMs = 24 * 60 * 60 * 1000;
    
    let days = 365; // default 1 year
    
    switch (period) {
      case '1w':
        days = 7;
        break;
      case '30d':
        days = 30;
        break;
      case '1m':
        days = 30;
        break;
      case '3m':
        days = 90;
        break;
      case '6m':
        days = 180;
        break;
      case '1y':
        days = 365;
        break;
      case '2y':
        days = 730;
        break;
      case '5y':
        days = 1825;
        break;
    }

    const startTime = now - (days * oneDayMs);
    return { days, startTime, endTime: now };
  }

  async fetchHistoricalData(
    symbol: string = 'BTCUSDT',
    interval: string = '1h',
    period: string = '1y'
  ): Promise<EnrichedCandleData[]> {
    const { startTime, endTime } = this.getTimeRangeFromPeriod(period);
    
    // Binance API has a limit of 1000 candles per request
    const maxCandlesPerRequest = 1000;
    const intervalMs = this.getIntervalMs(interval);
    
    let currentStartTime = startTime;
    const allCandles: CandleData[] = [];
    
    while (currentStartTime < endTime) {
      const currentEndTime = Math.min(
        currentStartTime + (maxCandlesPerRequest * intervalMs),
        endTime
      );
      
      const candles = await this.fetchKlines(
        symbol,
        interval,
        maxCandlesPerRequest,
        currentStartTime,
        currentEndTime
      );
      
      allCandles.push(...candles);
      currentStartTime = currentEndTime;
      
      // Rate limiting: wait 100ms between requests
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    // CRITICAL: Pine Script fetches OI and Funding in 1H timeframe (oi_tf = "60", fund_tf = "60")
    // Even when the chart is on 1D, we need 1H OI data to calculate 24h variations correctly
    console.log(`[BinanceService] Fetching OI in 1H timeframe for ${interval} chart (Pine Script approach)...`);
    
    const [oiData1H, fundingData] = await Promise.all([
      this.fetchOpenInterestHistory1H(symbol, startTime, endTime),
      this.fetchFundingRateHistory(symbol, startTime, endTime)
    ]);

    console.log(`[BinanceService] Got ${oiData1H.length} OI data points in 1H, ${fundingData.length} funding points`);

    // Calculate technical indicators with real PX-OI data using 1H OI
    return this.enrichWithIndicators(allCandles, oiData1H, fundingData);
  }

  // NEW: Fetch OI specifically in 1H timeframe (as Pine Script does with oi_tf = "60")
  async fetchOpenInterestHistory1H(
    symbol: string,
    startTime: number,
    endTime: number
  ): Promise<OpenInterestData[]> {
    try {
      const results: OpenInterestData[] = [];
      let currentStart = startTime;
      // Request 500 records of 1H = ~20 days per request
      const windowMs = 20 * 24 * 60 * 60 * 1000;

      while (currentStart < endTime) {
        const currentEnd = Math.min(currentStart + windowMs, endTime);
        const params = new URLSearchParams({
          symbol,
          period: '1h', // ALWAYS 1H as per Pine Script oi_tf_override
          startTime: currentStart.toString(),
          endTime: currentEnd.toString(),
          limit: '500'
        });

        const response = await fetch(`${this.futuresDataUrl}/openInterestHist?${params}`);
        if (!response.ok) {
          console.warn(`OI 1H data unavailable for ${symbol}: ${response.status}`);
          break;
        }

        const data = await response.json();
        results.push(
          ...data.map((item: any) => ({
            timestamp: parseInt(item.timestamp),
            openInterest: parseFloat(item.sumOpenInterest),
            symbol
          }))
        );

        currentStart = currentEnd + 1;
        await new Promise((r) => setTimeout(r, 100));
      }

      results.sort((a, b) => a.timestamp - b.timestamp);
      console.log(`[BinanceService] Fetched ${results.length} OI 1H data points for ${symbol}`);
      return results;
    } catch (error) {
      console.warn('Error fetching OI 1H data:', error);
      return [];
    }
  }

  async fetchOpenInterestHistory(
    symbol: string,
    period: string = '1h',
    startTime: number,
    endTime: number
  ): Promise<OpenInterestData[]> {
    try {
      const results: OpenInterestData[] = [];
      let currentStart = startTime;
      const windowMs = 24 * 60 * 60 * 1000; // 24 hours per request (500 * 5m = ~42h safe)
      
      // For periods longer than 7 days, use larger intervals to reduce requests
      const adjustedPeriod = (endTime - startTime) > (7 * 24 * 60 * 60 * 1000) ? '1h' : '5m';

      while (currentStart < endTime) {
        const currentEnd = Math.min(currentStart + windowMs, endTime);
        const params = new URLSearchParams({
          symbol,
          period: adjustedPeriod,
          startTime: currentStart.toString(),
          endTime: currentEnd.toString(),
          limit: '500'
        });

        const response = await fetch(`${this.futuresDataUrl}/openInterestHist?${params}`);
        if (!response.ok) {
          console.warn(`OI data unavailable for ${symbol} at ${new Date(currentStart).toISOString()}: ${response.status}`);
          break;
        }

        const data = await response.json();
        results.push(
          ...data.map((item: any) => ({
            timestamp: parseInt(item.timestamp),
            openInterest: parseFloat(item.sumOpenInterest),
            symbol
          }))
        );

        currentStart = currentEnd + 1;
        // Respect rate limits with small delay
        await new Promise((r) => setTimeout(r, 100));
      }

      // Sort by timestamp to ensure chronological order
      results.sort((a, b) => a.timestamp - b.timestamp);
      console.log(`Fetched ${results.length} OI data points for ${symbol} over ${Math.round((endTime - startTime) / (24 * 60 * 60 * 1000))} days`);
      return results;
    } catch (error) {
      console.warn('Error fetching OI data:', error);
      return [];
    }
  }

  async fetchFundingRateHistory(
    symbol: string,
    startTime: number,
    endTime: number
  ): Promise<FundingData[]> {
    try {
      const results: FundingData[] = [];
      let currentStart = startTime;
      const windowMs = 90 * 24 * 60 * 60 * 1000; // 90 days per request (safe under 1000 records)

      while (currentStart < endTime) {
        const currentEnd = Math.min(currentStart + windowMs, endTime);
        const params = new URLSearchParams({
          symbol,
          startTime: currentStart.toString(),
          endTime: currentEnd.toString(),
          limit: '1000'
        });

        const response = await fetch(`${this.futuresUrl}/fundingRate?${params}`);
        if (!response.ok) {
          console.warn(`Funding data unavailable for ${symbol}: ${response.status}`);
          break;
        }

        const data = await response.json();
        results.push(
          ...data.map((item: any) => ({
            timestamp: parseInt(item.fundingTime),
            fundingRate: parseFloat(item.fundingRate),
            fundingTime: parseInt(item.fundingTime)
          }))
        );

        currentStart = currentEnd + 1;
        // small delay to respect rate limits
        await new Promise((r) => setTimeout(r, 100));
      }

      // Ensure results are time-ordered
      results.sort((a, b) => a.timestamp - b.timestamp);
      return results;
    } catch (error) {
      console.warn('Error fetching funding data:', error);
      return [];
    }
  }

  async fetchTopTraderLongShortRatio(
    symbol: string,
    period: '5m' | '15m' | '30m' = '5m',
    limit: number = 60
  ): Promise<TopTraderRatioData[]> {
    try {
      const params = new URLSearchParams({
        symbol,
        period,
        limit: limit.toString()
      });

      const response = await fetch(`${this.futuresDataUrl}/topLongShortPositionRatio?${params}`);
      if (!response.ok) {
        throw new Error(`Top Trader Ratio API error: ${response.status}`);
      }

      const data = await response.json();
      return data.map((item: any) => ({
        timestamp: parseInt(item.timestamp),
        longAccount: parseFloat(item.longAccount),
        shortAccount: parseFloat(item.shortAccount),
        longShortRatio: parseFloat(item.longShortRatio)
      }));
    } catch (error) {
      console.error('Error fetching top trader ratio:', error);
      return [];
    }
  }

  async fetchLongShortRatio(
    symbol: string,
    period: '5m' | '15m' | '30m' = '5m',
    limit: number = 60
  ): Promise<TopTraderRatioData[]> {
    try {
      const params = new URLSearchParams({
        symbol,
        period,
        limit: limit.toString()
      });

      const response = await fetch(`${this.futuresDataUrl}/globalLongShortAccountRatio?${params}`);
      if (!response.ok) {
        throw new Error(`Long/Short Ratio API error: ${response.status}`);
      }

      const data = await response.json();
      return data.map((item: any) => ({
        timestamp: parseInt(item.timestamp),
        longAccount: parseFloat(item.longAccount),
        shortAccount: parseFloat(item.shortAccount),
        longShortRatio: parseFloat(item.longShortRatio)
      }));
    } catch (error) {
      console.error('Error fetching long/short ratio:', error);
      return [];
    }
  }

  async fetchLiquidationOrders(
    symbol: string,
    startTime?: number,
    endTime?: number,
    limit: number = 100
  ): Promise<LiquidationData[]> {
    try {
      const params = new URLSearchParams({
        symbol,
        limit: limit.toString()
      });

      if (startTime) params.append('startTime', startTime.toString());
      if (endTime) params.append('endTime', endTime.toString());

      const response = await fetch(`${this.futuresUrl}/forceOrders?${params}`);
      if (!response.ok) {
        throw new Error(`Liquidation Orders API error: ${response.status}`);
      }

      const data = await response.json();
      return data.map((item: any) => ({
        timestamp: parseInt(item.time),
        side: item.side,
        price: parseFloat(item.price),
        quantity: parseFloat(item.origQty),
        totalUSD: parseFloat(item.price) * parseFloat(item.origQty)
      }));
    } catch (error) {
      console.error('Error fetching liquidation orders:', error);
      return [];
    }
  }

  async fetchPremiumIndex(symbol: string): Promise<PremiumIndexResponse | null> {
    try {
      const params = new URLSearchParams({ symbol });
      const response = await fetch(`${this.futuresUrl}/premiumIndex?${params}`);
      
      if (!response.ok) {
        throw new Error(`Premium Index API error: ${response.status}`);
      }

      const data = await response.json();
      return {
        markPrice: parseFloat(data.markPrice),
        indexPrice: parseFloat(data.indexPrice),
        premiumRate: parseFloat(data.lastFundingRate),
        fundingRate: parseFloat(data.lastFundingRate),
        nextFundingTime: parseInt(data.nextFundingTime)
      };
    } catch (error) {
      console.error('Error fetching premium index:', error);
      return null;
    }
  }

  private getIntervalMs(interval: string): number {
    const intervalMap: Record<string, number> = {
      '1m': 60 * 1000,
      '3m': 3 * 60 * 1000,
      '5m': 5 * 60 * 1000,
      '15m': 15 * 60 * 1000,
      '30m': 30 * 60 * 1000,
      '1h': 60 * 60 * 1000,
      '2h': 2 * 60 * 60 * 1000,
      '4h': 4 * 60 * 60 * 1000,
      '6h': 6 * 60 * 60 * 1000,
      '8h': 8 * 60 * 60 * 1000,
      '12h': 12 * 60 * 60 * 1000,
      '1d': 24 * 60 * 60 * 1000,
      '3d': 3 * 24 * 60 * 60 * 1000,
      '1w': 7 * 24 * 60 * 60 * 1000,
      '1M': 30 * 24 * 60 * 60 * 1000
    };
    
    return intervalMap[interval] || 60 * 60 * 1000; // default to 1h
  }

  private enrichWithIndicators(
    candles: CandleData[], 
    oiData: OpenInterestData[] = [], 
    fundingData: FundingData[] = []
  ): EnrichedCandleData[] {
    const closes = candles.map(c => c.close);
    const highs = candles.map(c => c.high);
    const lows = candles.map(c => c.low);
    
    // Calculate indicators
    const sma20 = this.calculateSMA(closes, 20);
    const sma50 = this.calculateSMA(closes, 50);
    const ema200 = this.calculateEMA(closes, 200);
    const rsi = this.calculateRSI(closes, 14);
    const atr = this.calculateATR(highs, lows, closes, 14);
    const { supertrend, inUptrend } = this.calculateSupertrend(highs, lows, closes, atr, 3.0);
    const bollinger = this.calculateBollingerBands(closes, 20, 2);

    return candles.map((candle, i) => {
      // Calculate real PX-OI data for this candle
      const pxoiData = this.calculatePXOIData(candles, i, oiData, fundingData);
      const currentOI = this.findClosestDataPoint(oiData, candle.timestamp)?.openInterest || 0;
      const currentFunding = this.findClosestDataPoint(fundingData, candle.timestamp)?.fundingRate || 0;
      
      return {
        ...candle,
        sma20: sma20[i] || 0,
        sma50: sma50[i] || 0,
        ema200: ema200[i] || 0,
        rsi: rsi[i] || 50,
        atr: atr[i] || 0,
        supertrend: supertrend[i] || candle.close,
        inUptrend: inUptrend[i] || false,
        bbUpper: bollinger.upper[i] || candle.close,
        bbMiddle: bollinger.middle[i] || candle.close,
        bbLower: bollinger.lower[i] || candle.close,
        // Real PX-OI data
        openInterest: currentOI,
        fundingRate: currentFunding,
        premiumIndex: 0, // Could be calculated if needed
        // Ensure all PXOIData properties are defined
        priceChange8h: pxoiData.priceChange8h || 0,
        priceChange12h: pxoiData.priceChange12h || 0,
        priceChange24h: pxoiData.priceChange24h || 0,
        oiChange8h: pxoiData.oiChange8h || 0,
        oiChange12h: pxoiData.oiChange12h || 0,
        oiChange24h: pxoiData.oiChange24h || 0,
        priceDiff8h: pxoiData.priceDiff8h || 0,
        priceDiff12h: pxoiData.priceDiff12h || 0,
        priceDiff24h: pxoiData.priceDiff24h || 0,
        funding24h: pxoiData.funding24h || 0,
        fundingDelta24h: pxoiData.fundingDelta24h || 0
      };
    });
  }

  private calculateSMA(values: number[], period: number): number[] {
    const sma = [];
    for (let i = 0; i < values.length; i++) {
      if (i < period - 1) {
        sma.push(values[i]);
      } else {
        const sum = values.slice(i - period + 1, i + 1).reduce((a, b) => a + b, 0);
        sma.push(sum / period);
      }
    }
    return sma;
  }

  private calculateEMA(values: number[], period: number): number[] {
    const k = 2 / (period + 1);
    const ema = [values[0]];
    for (let i = 1; i < values.length; i++) {
      ema.push(values[i] * k + ema[i - 1] * (1 - k));
    }
    return ema;
  }

  private calculateRSI(values: number[], period: number): number[] {
    const rsi = [];
    for (let i = 0; i < values.length; i++) {
      if (i < period) {
        rsi.push(50);
        continue;
      }
      
      const changes = [];
      for (let j = i - period + 1; j <= i; j++) {
        changes.push(values[j] - values[j - 1]);
      }
      
      const gains = changes.filter(change => change > 0);
      const losses = changes.filter(change => change < 0).map(Math.abs);
      
      const avgGain = gains.length > 0 ? gains.reduce((a, b) => a + b, 0) / period : 0;
      const avgLoss = losses.length > 0 ? losses.reduce((a, b) => a + b, 0) / period : 0;
      
      if (avgLoss === 0) {
        rsi.push(100);
      } else {
        const rs = avgGain / avgLoss;
        rsi.push(100 - (100 / (1 + rs)));
      }
    }
    return rsi;
  }

  private calculateATR(highs: number[], lows: number[], closes: number[], period: number): number[] {
    const atr = [];
    for (let i = 0; i < closes.length; i++) {
      if (i < period) {
        atr.push(highs[i] - lows[i]);
        continue;
      }
      
      const trueRanges = [];
      for (let j = i - period + 1; j <= i; j++) {
        const highLow = highs[j] - lows[j];
        const highClose = j > 0 ? Math.abs(highs[j] - closes[j - 1]) : 0;
        const lowClose = j > 0 ? Math.abs(lows[j] - closes[j - 1]) : 0;
        trueRanges.push(Math.max(highLow, highClose, lowClose));
      }
      
      atr.push(trueRanges.reduce((a, b) => a + b, 0) / period);
    }
    return atr;
  }

  private calculateSupertrend(
    highs: number[], 
    lows: number[], 
    closes: number[], 
    atr: number[], 
    factor: number
  ): { supertrend: number[], inUptrend: boolean[] } {
    const supertrend = [];
    const inUptrend = [];
    
    for (let i = 0; i < closes.length; i++) {
      const hl2 = (highs[i] + lows[i]) / 2;
      const upperBand = hl2 + factor * atr[i];
      const lowerBand = hl2 - factor * atr[i];
      
      if (i === 0) {
        inUptrend.push(true);
        supertrend.push(lowerBand);
        continue;
      }
      
      let currentUptrend = inUptrend[i - 1];
      let currentSupertrend = supertrend[i - 1];
      
      if (closes[i] > upperBand) {
        currentUptrend = true;
      } else if (closes[i] < lowerBand) {
        currentUptrend = false;
      }
      
      if (currentUptrend) {
        currentSupertrend = Math.max(lowerBand, currentUptrend === inUptrend[i - 1] ? supertrend[i - 1] : lowerBand);
      } else {
        currentSupertrend = Math.min(upperBand, currentUptrend === inUptrend[i - 1] ? supertrend[i - 1] : upperBand);
      }
      
      inUptrend.push(currentUptrend);
      supertrend.push(currentSupertrend);
    }
    
    return { supertrend, inUptrend };
  }

  private calculateBollingerBands(values: number[], period: number, stdDev: number): {
    upper: number[];
    middle: number[];
    lower: number[];
  } {
    const upper = [];
    const middle = [];
    const lower = [];
    
    for (let i = 0; i < values.length; i++) {
      if (i < period - 1) {
        upper.push(values[i]);
        middle.push(values[i]);
        lower.push(values[i]);
        continue;
      }
      
      const slice = values.slice(i - period + 1, i + 1);
      const sma = slice.reduce((sum, val) => sum + val, 0) / period;
      const variance = slice.reduce((sum, val) => sum + Math.pow(val - sma, 2), 0) / period;
      const std = Math.sqrt(variance);
      
      upper.push(sma + stdDev * std);
      middle.push(sma);
      lower.push(sma - stdDev * std);
    }
    
    return { upper, middle, lower };
  }

  // Helper to find closest data point by timestamp
  private findClosestDataPoint<T extends { timestamp: number }>(data: T[], timestamp: number): T | null {
    if (data.length === 0) return null;
    
    let closest = data[0];
    let minDiff = Math.abs(data[0].timestamp - timestamp);
    
    for (const point of data) {
      const diff = Math.abs(point.timestamp - timestamp);
      if (diff < minDiff) {
        minDiff = diff;
        closest = point;
      }
    }
    
    // Accept up to 8 hours difference to accommodate 8h funding intervals
    return minDiff <= 8 * 60 * 60 * 1000 ? closest : null;
  }

  // Calculate PX-OI specific data (variations and differences)
  // Pine Script: uses 1H timeframe for OI/Funding, calculates 24h using 24 bars of 1H data
  private calculatePXOIData(
    candles: CandleData[], 
    currentIndex: number, 
    oiData: OpenInterestData[], 
    fundingData: FundingData[]
  ): Partial<PXOIData> {
    const current = candles[currentIndex];
    const currentTime = current.timestamp;
    
    // For 1D candles, we still look 24h back for price comparison
    // But OI/Funding use timestamp-based lookback (24h = 24 * 60 * 60 * 1000 ms)
    const prev = currentIndex > 0 ? candles[currentIndex - 1] : current;
    
    // Price change: for daily candles, compare current close vs previous day close
    const priceChange24h = currentIndex > 0 
      ? ((current.close - prev.close) / prev.close) * 100 
      : 0;
    
    // For 8h/12h, interpolate or use same day data
    const priceChange8h = priceChange24h / 3;
    const priceChange12h = priceChange24h / 2;

    // OI changes using 1H data with proper timestamp lookback
    // Pine Script: pctChangeN(oi, b24) where b24 = 24 bars for 1H data
    const currentOI = this.findClosestDataPoint(oiData, currentTime)?.openInterest || 0;
    const oi8hAgo = this.findClosestDataPoint(oiData, currentTime - (8 * 60 * 60 * 1000))?.openInterest || currentOI;
    const oi12hAgo = this.findClosestDataPoint(oiData, currentTime - (12 * 60 * 60 * 1000))?.openInterest || currentOI;
    const oi24hAgo = this.findClosestDataPoint(oiData, currentTime - (24 * 60 * 60 * 1000))?.openInterest || currentOI;

    const oiChange8h = oi8hAgo && oi8hAgo !== currentOI ? ((currentOI - oi8hAgo) / oi8hAgo) * 100 : 0;
    const oiChange12h = oi12hAgo && oi12hAgo !== currentOI ? ((currentOI - oi12hAgo) / oi12hAgo) * 100 : 0;
    const oiChange24h = oi24hAgo && oi24hAgo !== currentOI ? ((currentOI - oi24hAgo) / oi24hAgo) * 100 : 0;

    // Funding calculation per Pine Script:
    // fundFetchPerHour: raw / 8 (converts 8h funding to per-hour)
    // fund_lvl_24h_bps: fundingPerHour * 24 * 10000
    // deltaFundingBpsN: (perHour - perHour[24]) * 24 * 10000
    const currentFundingRaw = this.findClosestDataPoint(fundingData, currentTime)?.fundingRate || 0;
    const funding24hAgoRaw = this.findClosestDataPoint(fundingData, currentTime - (24 * 60 * 60 * 1000))?.fundingRate || currentFundingRaw;
    
    // Convert to per-hour (funding is 8h rate)
    const fundingPerHour = currentFundingRaw / 8;
    const funding24hAgoPerHour = funding24hAgoRaw / 8;
    
    // 24h funding level in bps: perHour * 24 * 10000
    const funding24h_bps = fundingPerHour * 24 * 10000;
    
    // Delta funding 24h in bps: (current - 24h ago) * 24 * 10000
    const fundingDelta24h_bps = (fundingPerHour - funding24hAgoPerHour) * 24 * 10000;

    // Debug logging for January 2026
    const dateStr = current.date?.slice(0, 10) || '';
    if (dateStr >= '2026-01-10' && dateStr <= '2026-01-20') {
      console.log(`[PXOI 1H] ${dateStr}:`, {
        price: current.close.toFixed(2),
        priceChange24h: priceChange24h.toFixed(2) + '%',
        currentOI: currentOI.toLocaleString(),
        oi24hAgo: oi24hAgo.toLocaleString(),
        oiChange24h: oiChange24h.toFixed(2) + '%',
        diff24h: (priceChange24h - oiChange24h).toFixed(2),
        fundingRaw: (currentFundingRaw * 100).toFixed(4) + '%',
        fundingPerHour: (fundingPerHour * 100).toFixed(6) + '%',
        funding24h_bps: funding24h_bps.toFixed(2) + ' bps',
        fundingDelta24h_bps: fundingDelta24h_bps.toFixed(2) + ' bps'
      });
    }

    return {
      priceChange8h,
      priceChange12h,
      priceChange24h,
      oiChange8h,
      oiChange12h,
      oiChange24h,
      priceDiff8h: priceChange8h - oiChange8h,
      priceDiff12h: priceChange12h - oiChange12h,
      priceDiff24h: priceChange24h - oiChange24h,
      funding24h: funding24h_bps / 10000, // Keep as decimal for compatibility, but now calculated correctly
      fundingDelta24h: fundingDelta24h_bps / 10000
    };
  }

  // Helper to calculate bars from hours based on candle interval
  private getBarsFromHours(hours: number, candles: CandleData[]): number {
    if (candles.length < 2) return 1;
    const intervalMs = candles[1].timestamp - candles[0].timestamp;
    return Math.round((hours * 60 * 60 * 1000) / intervalMs);
  }

  // Helper to calculate percentage change
  private calculatePercentChange(candles: CandleData[], currentIndex: number, barsBack: number): number {
    const pastIndex = currentIndex - barsBack;
    if (pastIndex < 0 || pastIndex >= candles.length) return 0;
    
    const current = candles[currentIndex].close;
    const past = candles[pastIndex].close;
    
    return past ? ((current - past) / past) * 100 : 0;
  }
}

export const binanceService = new BinanceService();